/**
 * Aura hover tooltip (#336) — shown by the shared {@link AuraChip} on hover,
 * anywhere it's reused (self buff/debuff bar #139, nameplates #137, unit
 * frames #138 — one hover behaviour at the shared-component level, not
 * duplicated per call site).
 *
 * Shows, at minimum: name (bold), description, remaining duration (or
 * "Permanent"), and stack count (only when > 1 — matches the chip's own
 * "only show a stack badge past 1" convention).
 *
 * POSITIONING: anchored `top: 100%` (below the chip) with its RIGHT edge
 * flush against the chip's right edge (`right: 0`), so it grows DOWN and
 * LEFT from the chip. This is deliberate for the primary caller (the
 * top-right self-aura bar, #139): a chip near the right viewport edge has
 * little/no room to the right, but plenty below and to the left, so
 * anchoring the tooltip's right edge (rather than centering it, which would
 * clip off-canvas) keeps it on-screen without any JS viewport measurement.
 *
 * PURELY COSMETIC. No sim/protocol/hash. `pointer-events: none` — the
 * tooltip itself is never interactive and must not steal the mouse-leave
 * that would otherwise dismiss it.
 */

import type { CSSProperties, ReactElement } from "react";

export interface AuraTooltipProps {
  readonly testId: string;
  readonly name: string;
  /** `null` — no authored description; shows a name-only tooltip. */
  readonly description: string | null;
  /** Whole seconds remaining, or `null` for a permanent (no-expiry) aura. */
  readonly remainingSeconds: number | null;
  readonly stacks: number;
}

export function AuraTooltip({
  testId,
  name,
  description,
  remainingSeconds,
  stacks,
}: AuraTooltipProps): ReactElement {
  const root: CSSProperties = {
    position: "absolute",
    top: "calc(100% + 6px)",
    right: 0,
    zIndex: 20,
    width: 220,
    padding: "8px 10px",
    background: "#11151b",
    border: "1px solid #3a4552",
    borderRadius: 6,
    color: "#dfe6ef",
    fontSize: 11,
    lineHeight: 1.4,
    textAlign: "left",
    pointerEvents: "none",
    boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
  };

  return (
    <div data-testid={testId} style={root}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{name}</div>
      {description !== null ? <div style={{ marginBottom: 4 }}>{description}</div> : null}
      <div style={{ color: "#8a93a0", fontSize: 10 }}>
        <span data-testid={`${testId}-duration`}>
          {remainingSeconds === null ? "Permanent" : `${String(remainingSeconds)}s remaining`}
        </span>
        {stacks > 1 ? (
          <span data-testid={`${testId}-stacks`}> · {stacks} stacks</span>
        ) : null}
      </div>
    </div>
  );
}
